import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Home, FolderOpen, DollarSign, Users, Shield, User, LogOut, Bell, Activity, Wrench, X, Megaphone, History, BookOpen, BarChart3, Inbox, Globe } from 'lucide-react';
import { HorizontalScroller } from './HorizontalScroller';
import { TodayEventsBanner } from './layout/TodayEventsBanner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Layout = ({ children }) => {
  const { user, logout, getAuthHeaders, hasPerm } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const notifRef = useRef(null);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnns, setDismissedAnns] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissed_announcements') || '[]'); } catch { return []; }
  });
  const [showAnnDialog, setShowAnnDialog] = useState(false);
  const [annMessage, setAnnMessage] = useState('');
  const [annSendEmail, setAnnSendEmail] = useState(false);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/projects', label: 'Projetos', icon: FolderOpen },
  ];
  // Pagamentos: always visible. Read-only for users without `access_payments_page`;
  // backend already restricts non-admins to their own records.
  navItems.push({ path: '/budgets', label: 'Pagamentos', icon: DollarSign });
  navItems.push({ path: '/tutoriais', label: 'Tutoriais', icon: BookOpen });
  navItems.push({ path: '/historico', label: 'Histórico', icon: History });
  navItems.push({ path: '/community', label: 'Comunidade', icon: Users });

  if (hasPerm('access_tools')) {
    navItems.push({ path: '/tools', label: 'Ferramentas', icon: Wrench });
  }

  if (hasPerm('access_finance')) {
    navItems.push({ path: '/finance', label: 'Financeiro', icon: BarChart3 });
  }
  if (hasPerm('access_budgets_landing')) {
    navItems.push({ path: '/orcamentos', label: 'Orçamentos', icon: Inbox });
  }

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  useEffect(() => {
    fetchNotifications();
    fetchAnnouncements();
    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=20`, { headers: getAuthHeaders() });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) { /* silent */ }
  };

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements`, { headers: getAuthHeaders() });
      setAnnouncements(res.data || []);
    } catch (err) { /* silent */ }
  };

  const dismissAnn = (id) => {
    const updated = [...dismissedAnns, id];
    setDismissedAnns(updated);
    localStorage.setItem('dismissed_announcements', JSON.stringify(updated));
  };

  const createAnnouncement = async () => {
    if (!annMessage.trim()) return;
    try {
      const fd = new FormData();
      fd.append('message', annMessage);
      fd.append('send_email_flag', annSendEmail);
      await axios.post(`${API}/announcements`, fd, { headers: getAuthHeaders() });
      setAnnMessage(''); setAnnSendEmail(false); setShowAnnDialog(false);
      fetchAnnouncements();
    } catch (err) { /* silent */ }
  };

  const deleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API}/announcements/${id}`, { headers: getAuthHeaders() });
      fetchAnnouncements();
    } catch (err) { /* silent */ }
  };

  const activeAnns = announcements.filter(a => a.active && !dismissedAnns.includes(a.id));

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers: getAuthHeaders() });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) { /* silent */ }
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { headers: getAuthHeaders() });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { /* silent */ }
  };

  const formatTimeAgo = (iso) => {
    try {
      const diff = Date.now() - new Date(iso).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'agora';
      if (mins < 60) return `${mins}min`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h`;
      const days = Math.floor(hrs / 24);
      return `${days}d`;
    } catch { return ''; }
  };

  const notifTypeIcon = (type) => {
    switch (type) {
      case 'project_assignment': return 'text-purple-400';
      case 'payment_created': return 'text-blue-400';
      case 'payment_completed': return 'text-green-400';
      case 'birthday_reminder': return 'text-amber-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 overflow-x-hidden">
      {/* Navigation — full-width so logo hugs the left */}
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex justify-between items-center gap-4">
            {/* Logo & Nav */}
            <div className="flex items-center gap-8">
              <Link to="/dashboard" className="shrink-0">
                <img 
                  src="https://customer-assets.emergentagent.com/job_dubsync-3/artifacts/jj7xe7zi_Screenshot_3.jpeg.png" 
                  alt="DUBLEIROS STUDIOS" 
                  className="h-10 w-auto"
                  data-testid="nav-logo"
                />
              </Link>
              
              <div className="hidden md:block flex-1 min-w-0">
                <HorizontalScroller
                  className="items-center gap-0.5 flex-nowrap"
                  fadeColor="from-slate-900"
                  innerProps={{ 'data-testid': 'desktop-nav-scroll' }}
                >
                  {navItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg transition-colors shrink-0 ${
                        isActive(item.path)
                          ? 'bg-teal-500/20 text-teal-400'
                          : 'text-slate-400 hover:text-white hover:bg-slate-800'
                      }`}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      <item.icon className="h-4 w-4" />
                      <span className="text-xs font-medium whitespace-nowrap">{item.label}</span>
                    </Link>
                  ))}

                  {hasPerm('access_admin_panel') && (
                    <Link
                      to="/admin"
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors shrink-0 ${
                        isActive('/admin')
                          ? 'bg-purple-500/20 text-purple-400'
                          : 'text-slate-400 hover:text-white hover:bg-slate-800'
                      }`}
                      data-testid="nav-admin"
                    >
                      <Shield className="h-4 w-4" />
                      <span className="text-xs font-medium whitespace-nowrap">Admin</span>
                    </Link>
                  )}
                  {hasPerm('access_activity_log') && (
                    <Link
                      to="/activity-log"
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors shrink-0 ${
                        isActive('/activity-log')
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'text-slate-400 hover:text-white hover:bg-slate-800'
                      }`}
                      data-testid="nav-activity-log"
                    >
                      <Activity className="h-4 w-4" />
                      <span className="text-xs font-medium whitespace-nowrap">Log</span>
                    </Link>
                  )}
                </HorizontalScroller>
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setShowNotifs(!showNotifs)}
                  className="relative p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                  data-testid="notification-bell"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-testid="notification-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {showNotifs && (
                  <div className="absolute right-0 top-full mt-2 w-80 max-h-96 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-50" data-testid="notification-dropdown">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
                      <span className="text-white font-semibold text-sm">Notificações</span>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs text-teal-400 hover:text-teal-300" data-testid="mark-all-read">
                          Marcar todas como lidas
                        </button>
                      )}
                    </div>
                    <div className="overflow-y-auto max-h-72">
                      {notifications.length === 0 ? (
                        <div className="text-center py-8 text-slate-500 text-sm">Nenhuma notificação</div>
                      ) : (
                        notifications.map(n => (
                          <Link
                            key={n.id}
                            to={n.link || '#'}
                            onClick={() => { if (!n.read) markRead(n.id); setShowNotifs(false); }}
                            className={`block px-4 py-3 border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors ${!n.read ? 'bg-slate-700/20' : ''}`}
                            data-testid={`notification-${n.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${!n.read ? 'bg-teal-400' : 'bg-slate-600'}`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${notifTypeIcon(n.type)}`}>{n.title}</p>
                                <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.message}</p>
                              </div>
                              <span className="text-xs text-slate-500 shrink-0">{formatTimeAgo(n.created_at)}</span>
                            </div>
                          </Link>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/profile"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  isActive('/profile')
                    ? 'bg-teal-500/20 text-teal-400'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid="nav-profile"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <User className="h-5 w-5" />
                )}
                <span className="hidden sm:inline text-sm font-medium">{user?.artist_name || user?.name?.split(' ')[0]}</span>
              </Link>
              
              <Link
                to="/"
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-teal-400 hover:text-teal-300 hover:bg-teal-500/10 transition-colors"
                data-testid="back-to-public-btn"
                title="Voltar para o site público (área do cliente)"
              >
                <Globe className="h-5 w-5" />
                <span className="hidden sm:inline text-sm font-medium">Site</span>
              </Link>

              <button
                onClick={logout}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
                <span className="hidden sm:inline text-sm font-medium">Sair</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation — horizontal scrollable bar with edge fades */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-sm border-t border-slate-800 z-40 safe-area-pb">
        <HorizontalScroller
          className="py-2 px-2 gap-1"
          fadeColor="from-slate-900"
          innerProps={{ 'data-testid': 'mobile-nav-scroll' }}
        >
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors shrink-0 min-w-[66px] snap-start ${
                isActive(item.path)
                  ? 'text-teal-400 bg-teal-500/10'
                  : 'text-slate-400'
              }`}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-[10px] font-medium whitespace-nowrap">{item.label}</span>
            </Link>
          ))}
          {hasPerm('access_admin_panel') && (
            <Link
              to="/admin"
              className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors shrink-0 min-w-[66px] snap-start ${
                isActive('/admin') ? 'text-purple-400 bg-purple-500/10' : 'text-slate-400'
              }`}
              data-testid="mobile-nav-admin"
            >
              <Shield className="h-5 w-5" />
              <span className="text-[10px] font-medium">Admin</span>
            </Link>
          )}
          {hasPerm('access_activity_log') && (
            <Link
              to="/activity-log"
              className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors shrink-0 min-w-[66px] snap-start ${
                isActive('/activity-log') ? 'text-amber-400 bg-amber-500/10' : 'text-slate-400'
              }`}
              data-testid="mobile-nav-log"
            >
              <Activity className="h-5 w-5" />
              <span className="text-[10px] font-medium">Log</span>
            </Link>
          )}
        </HorizontalScroller>
      </div>

      {/* Main Content — centered with a comfortable max width so nothing gets cut off */}
      <main className="pb-20 md:pb-0 max-w-[1400px] mx-auto w-full">
        <TodayEventsBanner getAuthHeaders={getAuthHeaders} />
        {children}
      </main>

      {/* Announcement Cards - Bottom Left */}
      {activeAnns.length > 0 && (
        <div className="fixed bottom-4 left-4 z-50 space-y-2 max-w-sm" data-testid="announcement-cards">
          {activeAnns.slice(0, 3).map(ann => (
            <div key={ann.id} className="bg-slate-800 border border-amber-500/40 rounded-xl p-4 shadow-2xl animate-in slide-in-from-left" data-testid={`ann-card-${ann.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex gap-2 items-start flex-1 min-w-0">
                  <Megaphone className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-white text-sm leading-snug">{ann.message}</p>
                    <p className="text-slate-500 text-xs mt-1">por {ann.author_name}</p>
                  </div>
                </div>
                <button onClick={() => dismissAnn(ann.id)} className="text-slate-500 hover:text-white shrink-0 p-0.5" data-testid={`dismiss-ann-${ann.id}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              {hasPerm('send_announcements') && (
                <button onClick={() => deleteAnnouncement(ann.id)} className="text-xs text-red-400 hover:text-red-300 mt-2" data-testid={`delete-ann-${ann.id}`}>
                  Excluir aviso
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Admin: Create Announcement Button */}
      {hasPerm('send_announcements') && (
        <>
          <button onClick={() => setShowAnnDialog(true)}
            className="fixed bottom-4 right-4 z-50 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-full p-3 shadow-lg transition-colors md:bottom-6 md:right-6"
            data-testid="create-announcement-btn" title="Criar Aviso">
            <Megaphone className="w-5 h-5" />
          </button>

          {showAnnDialog && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => setShowAnnDialog(false)}>
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl my-auto max-h-[calc(100vh-2rem)] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="announcement-dialog">
                <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2"><Megaphone className="w-5 h-5 text-amber-400" /> Novo Aviso</h3>
                <textarea value={annMessage} onChange={e => setAnnMessage(e.target.value)}
                  placeholder="Escreva o aviso para todos..."
                  className="w-full bg-slate-900 border border-slate-600 text-white p-3 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-amber-400 text-sm"
                  rows={4} data-testid="announcement-message" />
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input type="checkbox" checked={annSendEmail} onChange={e => setAnnSendEmail(e.target.checked)} className="accent-amber-500 w-4 h-4" data-testid="announcement-send-email" />
                  <span className="text-slate-300 text-sm">Enviar também por e-mail para todos</span>
                </label>
                <div className="flex gap-3 mt-4 justify-end">
                  <button onClick={() => setShowAnnDialog(false)} className="px-4 py-2 text-slate-400 hover:text-white text-sm">Cancelar</button>
                  <button onClick={createAnnouncement} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium text-sm" data-testid="announcement-submit">Publicar Aviso</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Layout;
